import React, { useState, useCallback, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { Box, Typography } from '@mui/material';
import WorkspaceTabs from './WorkspaceTabs';
import MetadataWorkspace from './MetadataWorkspace';
import HomeDashboard from './HomeDashboard';
import AtomResponsePanel from '../AtomResponsePanel';
import WorkspaceService from '../../services/WorkspaceService';
import AuthService from '../../services/AuthService';

// Pinned, synthetic first tab -- not a real workspace_registry entry, never
// persisted to localStorage alongside real tabs. Always rendered first when
// there's at least one chat-produced table this session. See
// project_atom_response_workspace_tab memory: the user's own framing was
// "like a TV screen" -- one persistent tab, chat panel never covered,
// results stack rather than replace.
const ATOM_RESPONSE_TAB_ID = 'atom-response';

const MAX_TABS = 8;
const TABS_STORAGE_KEY_BASE = 'fastStudio.workspacePanel.tabs';

// Scope the persisted-tabs key by user id (falls back to email, matching the
// pattern used in DashboardService/FlexStudio) -- without this, one user's
// open workspace tabs (including role-gated ones like Platform Admin) get
// restored into WHOEVER logs in next in this browser, since localStorage is
// shared across all tabs/sessions on the same origin.
const getTabsStorageKey = () => {
  const user = AuthService.getUser();
  const scope = (user && (user.id || user.email)) || 'anonymous';
  return `${TABS_STORAGE_KEY_BASE}_${scope}`;
};

// Called on logout, while AuthService.getUser() still resolves to the
// outgoing user, so the correct scoped key gets cleared. Also sweeps any
// legacy unscoped key and other users' leftover entries in this browser.
export const clearPersistedWorkspaceTabs = () => {
  try {
    localStorage.removeItem(getTabsStorageKey());
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(TABS_STORAGE_KEY_BASE)) {
        localStorage.removeItem(key);
      }
    });
  } catch {
    // localStorage unavailable -- nothing to clear
  }
};

/**
 * WorkspacePanel - Container for multiple workspace tab instances.
 *
 * Manages open tabs, fetches workspace definitions, and renders
 * MetadataWorkspace for each tab. Tabs stay mounted (display:none)
 * when inactive to preserve state.
 *
 * Open tabs + which one is focused persist across a page reload (localStorage,
 * see the restore/persist effects below) — only the lightweight tab
 * descriptor (tabId, workspaceType, label, navContext) is saved, never the
 * full `definition` JSON, which is re-fetched fresh on restore.
 */
const WorkspacePanel = forwardRef(({ colors, atomResponseResults = [], highlightedResultId, onHighlightHandled }, ref) => {
  const [tabs, setTabs] = useState([]);
  const [activeTabId, setActiveTabId] = useState(null);
  // Scroll-target refs for AtomResponsePanel's stacked results, keyed by result id.
  const atomResponseRefs = useRef({});
  // Gates the persist effect until the restore-on-mount attempt has finished,
  // so we don't immediately overwrite the saved tabs with an empty array
  // while the async restore is still fetching definitions.
  const restoredRef = useRef(false);
  // Bumped whenever the selected warehouse/connected system changes (see
  // refreshAllTabs below) -- included in each MetadataWorkspace's key so
  // React fully remounts it instead of trying to patch in place. A remount
  // is deliberate here: it's the only way to guarantee every cached bit of
  // state inside a tab (grid rows, dropdown options, field metadata) is
  // re-fetched under the new system, not just the master grid data a
  // regular Refresh click would cover.
  const [refreshGeneration, setRefreshGeneration] = useState(0);

  // Cache workspace definitions
  const [definitionCache, setDefinitionCache] = useState({});

  /**
   * Open a workspace tab. If same type already exists, focus it.
   * If allowMultiple is true (default), allows multiple tabs of same type.
   */
  // The tabs as they are now, not as this callback last saw them, and the screens being opened: two opens of the same
  // screen close together (or one from a stale callback) used to both pass the "already open?" check while the
  // first was still fetching its definition, and both added a tab.
  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;
  const openingRef = useRef({});

  const openWorkspace = useCallback(async (workspaceCode, workspaceTitle, allowMultiple = true, navContext = null) => {
    if (!allowMultiple && openingRef.current[workspaceCode]) {
      await openingRef.current[workspaceCode];
    }
    // If not allowing multiple, check for existing tab of same type
    if (!allowMultiple) {
      const existing = tabsRef.current.find((t) => t.workspaceType === workspaceCode);
      if (existing) {
        // Update navContext on existing tab if provided
        if (navContext) {
          setTabs(prev => prev.map(t => t.tabId === existing.tabId ? { ...t, navContext } : t));
        }
        setActiveTabId(existing.tabId);
        return;
      }
    }

    if (tabsRef.current.length >= MAX_TABS) {
      // Could show a snackbar here
      console.warn('Maximum workspace tabs reached');
      return;
    }

    // Always fetch a fresh definition on open rather than trusting the cache.
    // Previously this short-circuited on definitionCache[workspaceCode] and
    // never refetched for the lifetime of the browser session -- so a
    // workspace_registry.definition change made server-side (e.g. an admin
    // fix, or an app deploy) stayed invisible to an already-open session
    // until a full page reload. Real bug hit live 2026-09-28: the Platform
    // Admin "Entities" tab's backend fix was restart-verified correct, but
    // the user's browser kept rendering the stale cached definition with no
    // Fields/Actions detail grids. definitionCache is now write-through only
    // (kept in case something else reads it), never read here as a gate.
    let definition;
    let finishOpening;
    if (!allowMultiple) openingRef.current[workspaceCode] = new Promise((resolve) => { finishOpening = resolve; });
    try {
      const result = await WorkspaceService.getWorkspaceDefinition(workspaceCode);
      definition = result.definition;
      if (typeof definition === 'string') {
        definition = JSON.parse(definition);
      }
      setDefinitionCache((prev) => ({ ...prev, [workspaceCode]: definition }));
    } catch (err) {
      console.error(`Failed to load workspace ${workspaceCode}:`, err);
      if (finishOpening) { delete openingRef.current[workspaceCode]; finishOpening(); }
      return;
    }

    // Generate label with instance number if duplicate
    const existingCount = tabsRef.current.filter((t) => t.workspaceType === workspaceCode).length;
    const label = existingCount > 0
      ? `${workspaceTitle || definition.title} (${existingCount + 1})`
      : workspaceTitle || definition.title;

    const tabId = `${workspaceCode}-${Date.now()}`;
    const newTab = {
      tabId,
      workspaceType: workspaceCode,
      label,
      definition,
      navContext,
    };

    tabsRef.current = [...tabsRef.current, newTab];
    setTabs((prev) => [...prev, newTab]);
    setActiveTabId(tabId);
    if (finishOpening) { delete openingRef.current[workspaceCode]; finishOpening(); }
  }, []); // reads the tabs through tabsRef, so it never acts on a stale list

  // Called by AtomApp right after switching the selected connected system --
  // keeps every open tab open (no confirmation prompt), just forces each one
  // to reload fresh under the newly selected system.
  const refreshAllTabs = useCallback(() => {
    setRefreshGeneration((g) => g + 1);
  }, []);

  // Focuses the pinned Atom Response tab -- called from AtomApp whenever a
  // new chat table arrives, or the user clicks a "View in workspace" link on
  // an older one. Does not touch `tabs`/localStorage -- this tab is synthetic,
  // never a real persisted workspace tab.
  const focusAtomResponse = useCallback(() => {
    setActiveTabId(ATOM_RESPONSE_TAB_ID);
  }, []);

  // Expose openWorkspace/refreshAllTabs to parent via ref
  useImperativeHandle(ref, () => ({
    openWorkspace,
    refreshAllTabs,
    focusAtomResponse,
  }), [openWorkspace, refreshAllTabs, focusAtomResponse]);

  // Restore last session's open tabs on mount. Only the lightweight
  // descriptor was saved (never `definition`) — each tab's JSON is re-fetched
  // fresh here, so a backend change to a workspace's structure is picked up
  // on reload rather than restoring a stale cached shape. A tab whose
  // workspace no longer exists (deleted/renamed) is silently dropped.
  useEffect(() => {
    let cancelled = false;

    const restore = async () => {
      let savedTabs = [];
      let savedActiveTabId = null;
      try {
        const raw = localStorage.getItem(getTabsStorageKey());
        if (raw) {
          const parsed = JSON.parse(raw);
          savedTabs = Array.isArray(parsed?.tabs) ? parsed.tabs : [];
          savedActiveTabId = parsed?.activeTabId ?? null;
        }
      } catch {
        savedTabs = [];
      }

      if (savedTabs.length === 0) {
        restoredRef.current = true;
        return;
      }

      // Defense in depth: getWorkspaceDefinition() itself has no role check,
      // so also cross-check restored tabs against the role-filtered
      // /available list before re-fetching definitions -- covers a tab
      // saved while the user had a role that's since been downgraded, not
      // just the cross-account case (fixed by scoping the storage key above).
      try {
        const available = await WorkspaceService.getAvailableWorkspaces();
        const allowedCodes = new Set((available || []).map((ws) => ws.workspaceCode));
        savedTabs = savedTabs.filter((t) => allowedCodes.has(t.workspaceType));
      } catch (err) {
        console.warn('Failed to validate restored tabs against available workspaces:', err);
        savedTabs = [];
      }

      if (cancelled) return;

      if (savedTabs.length === 0) {
        restoredRef.current = true;
        return;
      }

      const results = await Promise.all(
        savedTabs.map(async (t) => {
          try {
            const result = await WorkspaceService.getWorkspaceDefinition(t.workspaceType);
            let definition = result.definition;
            if (typeof definition === 'string') definition = JSON.parse(definition);
            return { ...t, definition };
          } catch (err) {
            console.warn(`Failed to restore workspace tab "${t.workspaceType}":`, err);
            return null;
          }
        })
      );

      if (cancelled) return;

      const restoredTabs = results.filter(Boolean);
      if (restoredTabs.length > 0) {
        setTabs(restoredTabs);
        const savedActive = restoredTabs.find((t) => t.tabId === savedActiveTabId);
        setActiveTabId(savedActive ? savedActive.tabId : restoredTabs[restoredTabs.length - 1].tabId);
      }
      restoredRef.current = true;
    };

    restore();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist the lightweight tab descriptors + focused tab whenever they
  // change, so a reload restores the same set of open workspaces. Gated on
  // restoredRef so the initial empty state doesn't clobber a saved session
  // before the restore effect above has had a chance to run.
  useEffect(() => {
    if (!restoredRef.current) return;
    try {
      const lightweight = tabs.map(({ tabId, workspaceType, label, navContext }) => ({
        tabId, workspaceType, label, navContext,
      }));
      localStorage.setItem(getTabsStorageKey(), JSON.stringify({ tabs: lightweight, activeTabId }));
    } catch {
      // localStorage unavailable — persistence is best-effort, not required
    }
  }, [tabs, activeTabId]);

  const closeTab = useCallback((tabId) => {
    setTabs((prev) => {
      const filtered = prev.filter((t) => t.tabId !== tabId);
      // If closing active tab, switch to last remaining tab or null
      if (activeTabId === tabId) {
        const closedIndex = prev.findIndex((t) => t.tabId === tabId);
        const newActive = filtered.length > 0
          ? filtered[Math.min(closedIndex, filtered.length - 1)]?.tabId
          : null;
        setActiveTabId(newActive);
      }
      return filtered;
    });
  }, [activeTabId]);

  const changeTab = useCallback((tabId) => {
    setActiveTabId(tabId);
  }, []);

  const hasAtomResponse = atomResponseResults.length > 0;
  // Pinned first, ahead of every real tab -- not part of `tabs`/localStorage.
  // Not closeable (no onTabClose entry point rendered for it -- see
  // WorkspaceTabs' own onTabClose contract) since results should persist for
  // the whole session, matching "it stays there" from the user's own framing.
  const displayTabs = hasAtomResponse
    ? [{ tabId: ATOM_RESPONSE_TAB_ID, workspaceType: 'ATOM_RESPONSE', label: 'Atom Response', pinned: true }, ...tabs]
    : tabs;

  // Empty state -- default KPI home dashboard. Was briefly suspected and
  // disabled 2026-09-28 while chasing a "whole page stuck spinning" report,
  // but the real cause turned out to be unrelated (a corrupted
  // localStorage waide_user cache -- see
  // feedback_waide_user_cache_corruption_permanent_spinner, now fixed).
  // Re-enabled once that was confirmed. See project_home_dashboard_kpi_widgets_idea.
  if (tabs.length === 0 && !hasAtomResponse) {
    return <HomeDashboard colors={colors} onOpenWorkspace={openWorkspace} />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: colors.background }}>
      {/* Tab bar */}
      <WorkspaceTabs
        tabs={displayTabs}
        activeTabId={activeTabId}
        onTabChange={changeTab}
        onTabClose={closeTab}
        colors={colors}
      />

      {/* Workspace content — all tabs rendered but only active visible */}
      <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        {hasAtomResponse && (
          <Box
            key={ATOM_RESPONSE_TAB_ID}
            sx={{
              display: activeTabId === ATOM_RESPONSE_TAB_ID ? 'flex' : 'none',
              flexDirection: 'column',
              position: 'absolute',
              top: 0, left: 0, right: 0, bottom: 0,
            }}
          >
            <AtomResponsePanel
              results={atomResponseResults}
              highlightedResultId={highlightedResultId}
              onHighlightHandled={onHighlightHandled}
              resultRefs={atomResponseRefs}
              colors={colors}
            />
          </Box>
        )}
        {tabs.map((tab) => (
          <Box
            key={tab.tabId}
            sx={{
              display: activeTabId === tab.tabId ? 'flex' : 'none',
              flexDirection: 'column',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          >
            <MetadataWorkspace
              key={`${tab.tabId}-${refreshGeneration}`}
              definition={tab.definition}
              workspaceCode={tab.workspaceType}
              colors={colors}
              navContext={tab.navContext}
              onOpenWorkspace={openWorkspace}
              isActive={activeTabId === tab.tabId}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
});

WorkspacePanel.displayName = 'WorkspacePanel';

export default WorkspacePanel;
